import * as THREE from 'three';
import { DEFAULT_WALL_THICKNESS_M } from './floorPlanDesignerState.js';
import {
  DEFAULT_DOOR_HEIGHT_METERS,
  DEFAULT_DOOR_WIDTH_METERS,
  DEFAULT_WINDOW_HEIGHT_METERS,
  DEFAULT_WINDOW_SILL_HEIGHT_METERS,
  DEFAULT_WINDOW_WIDTH_METERS,
  resolveOpeningT,
} from './floorPlanWallGeometry.js';

const DEFAULT_MATERIALS = {
  wood: new THREE.MeshStandardMaterial({ color: '#8b5a2b' }),
  darkWood: new THREE.MeshStandardMaterial({ color: '#5f3b1f' }),
  fabric: new THREE.MeshStandardMaterial({ color: '#64748b' }),
  mattress: new THREE.MeshStandardMaterial({ color: '#e2e8f0' }),
  glass: new THREE.MeshStandardMaterial({
    color: '#93c5fd',
    transparent: true,
    opacity: 0.65,
    roughness: 0.08,
  }),
  metal: new THREE.MeshStandardMaterial({ color: '#94a3b8', metalness: 0.35, roughness: 0.35 }),
};

function tagObject3D(object3D, data) {
  object3D.userData.objectId = data.id;
  object3D.userData.objectType = data.type;
  if (data.category) object3D.userData.category = data.category;
  object3D.traverse?.((child) => {
    child.userData.objectId = data.id;
    child.userData.objectType = data.type;
    if (data.category) child.userData.category = data.category;
  });
  return object3D;
}

function box(name, width, height, depth, material, position = [0, height / 2, 0]) {
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(width, height, depth), material);
  mesh.name = name;
  mesh.position.set(position[0], position[1], position[2]);
  return mesh;
}

function positiveNumber(value, fallback) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

function nonNegativeNumber(value, fallback) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
}

export function createDoor3D(data, materials = DEFAULT_MATERIALS) {
  const openingWidth = positiveNumber(data.width, 0.9);
  const openingHeight = positiveNumber(data.height, 2.1);
  const wallThickness = positiveNumber(
    data.depth ?? data.wallThickness ?? data.frameDepth ?? data.thickness,
    DEFAULT_WALL_THICKNESS_M,
  );
  const doorState =
    data.doorState === 'open' || data.doorState === 'closed' || data.doorState === 'frame-only'
      ? data.doorState
      : 'closed';
  const showPanel = data.showPanel !== false && doorState !== 'frame-only';
  const openAngle = doorState === 'open'
    ? 90
    : Number.isFinite(Number(data.openAngle))
      ? Number(data.openAngle)
      : 0;
  const hingeSide = data.hingeSide === 'right' ? 'right' : (data.swingDirection === 'right' ? 'right' : 'left');
  const swingSide = data.swingSide === 'outside' ? 'outside' : 'inside';
  const hingeSign = hingeSide === 'right' ? -1 : 1;
  const swingSign = swingSide === 'outside' ? -hingeSign : hingeSign;
  const group = new THREE.Group();
  group.name = `door-${data.id}`;

  const frameSideWidth = Math.min(0.06, openingWidth / 4, openingHeight / 4);
  const panelWidth = Math.max(0.01, openingWidth - frameSideWidth * 2);
  const panelHeight = Math.max(0.01, openingHeight - frameSideWidth);
  group.add(box(
    'door-frame-left',
    frameSideWidth,
    openingHeight,
    wallThickness,
    materials.darkWood,
    [-openingWidth / 2 + frameSideWidth / 2, openingHeight / 2, 0],
  ));
  group.add(box(
    'door-frame-right',
    frameSideWidth,
    openingHeight,
    wallThickness,
    materials.darkWood,
    [openingWidth / 2 - frameSideWidth / 2, openingHeight / 2, 0],
  ));
  group.add(box(
    'door-frame-top',
    openingWidth,
    frameSideWidth,
    wallThickness,
    materials.darkWood,
    [0, openingHeight - frameSideWidth / 2, 0],
  ));

  if (showPanel) {
    const hingeX =
      hingeSide === 'right'
        ? openingWidth / 2 - frameSideWidth
        : -openingWidth / 2 + frameSideWidth;
    const panelPivot = new THREE.Group();
    panelPivot.name = 'door-panel-pivot';
    panelPivot.position.set(hingeX, 0, 0);
    panelPivot.rotation.y = THREE.MathUtils.degToRad(openAngle) * swingSign;
    const panelOffsetX = hingeSide === 'right' ? -panelWidth / 2 : panelWidth / 2;
    const panel = box(
      'door-panel',
      panelWidth,
      panelHeight,
      wallThickness,
      materials.wood,
      [panelOffsetX, panelHeight / 2, 0],
    );
    panelPivot.add(panel);
    const handle = box('door-handle', 0.05, 0.05, 0.08, materials.metal, [
      panelOffsetX + (hingeSide === 'right' ? 0.12 : -0.12),
      panelHeight * 0.52,
      wallThickness / 2 + 0.02,
    ]);
    panelPivot.add(handle);
    group.add(panelPivot);
  }

  return tagObject3D(group, data);
}

export function createWindow3D(data, materials = DEFAULT_MATERIALS) {
  const openingWidth = positiveNumber(data.width, 1.2);
  const openingHeight = positiveNumber(data.height, 1.2);
  const sillHeight = nonNegativeNumber(data.sillHeight, 0.9);
  const frameDepth = positiveNumber(
    data.frameDepth ?? data.depth ?? data.wallThickness ?? data.thickness,
    DEFAULT_WALL_THICKNESS_M,
  );
  const glassDepth = positiveNumber(data.glassDepth, Math.max(0.02, frameDepth * 0.35));
  const group = new THREE.Group();
  group.name = `window-${data.id}`;
  const frameSideWidth = Math.min(0.06, openingWidth / 4, openingHeight / 4);
  const centerY = sillHeight + openingHeight / 2;
  const glassWidth = Math.max(0.01, openingWidth - frameSideWidth * 2);
  const glassHeight = Math.max(0.01, openingHeight - frameSideWidth * 2);

  group.add(box(
    'window-frame-left',
    frameSideWidth,
    openingHeight,
    frameDepth,
    materials.darkWood,
    [-openingWidth / 2 + frameSideWidth / 2, centerY, 0],
  ));
  group.add(box(
    'window-frame-right',
    frameSideWidth,
    openingHeight,
    frameDepth,
    materials.darkWood,
    [openingWidth / 2 - frameSideWidth / 2, centerY, 0],
  ));
  group.add(box(
    'window-frame-bottom',
    openingWidth,
    frameSideWidth,
    frameDepth,
    materials.darkWood,
    [0, sillHeight + frameSideWidth / 2, 0],
  ));
  group.add(box(
    'window-frame-top',
    openingWidth,
    frameSideWidth,
    frameDepth,
    materials.darkWood,
    [0, sillHeight + openingHeight - frameSideWidth / 2, 0],
  ));
  group.add(box('window-glass', glassWidth, glassHeight, glassDepth, materials.glass, [0, centerY, 0]));

  return tagObject3D(group, data);
}

export function createSimpleSofa(data, materials = DEFAULT_MATERIALS) {
  const group = new THREE.Group();
  const { width = 2, depth = 0.9, height = 0.8 } = data;
  group.add(box('sofa-seat', width, height * 0.35, depth, materials.fabric, [0, height * 0.175, 0]));
  group.add(box('sofa-back', width, height * 0.75, depth * 0.18, materials.fabric, [0, height * 0.375, depth * 0.41]));
  group.add(box('sofa-arm-left', width * 0.08, height * 0.55, depth, materials.fabric, [-width * 0.46, height * 0.275, 0]));
  group.add(box('sofa-arm-right', width * 0.08, height * 0.55, depth, materials.fabric, [width * 0.46, height * 0.275, 0]));
  return tagObject3D(group, data);
}

export function createSimpleBed(data, materials = DEFAULT_MATERIALS) {
  const group = new THREE.Group();
  const { width = 2, depth = 1.6, height = 0.55 } = data;
  group.add(box('bed-base', width, height * 0.35, depth, materials.darkWood, [0, height * 0.175, 0]));
  group.add(box('bed-mattress', width * 0.96, height * 0.32, depth * 0.94, materials.mattress, [0, height * 0.51, 0]));
  group.add(box('bed-headboard', width, height, depth * 0.08, materials.darkWood, [0, height / 2, depth * 0.46]));
  return tagObject3D(group, data);
}

export function createSimpleTable(data, materials = DEFAULT_MATERIALS) {
  const group = new THREE.Group();
  const { width = 1.4, depth = 0.8, height = 0.75 } = data;
  group.add(box('table-top', width, 0.08, depth, materials.wood, [0, height, 0]));
  const legW = 0.07;
  for (const x of [-width / 2 + legW, width / 2 - legW]) {
    for (const z of [-depth / 2 + legW, depth / 2 - legW]) {
      group.add(box('table-leg', legW, height, legW, materials.darkWood, [x, height / 2, z]));
    }
  }
  return tagObject3D(group, data);
}

export function createSimpleChair(data, materials = DEFAULT_MATERIALS) {
  const group = new THREE.Group();
  const { width = 0.5, depth = 0.55, height = 0.9 } = data;
  group.add(box('chair-seat', width, 0.08, depth, materials.wood, [0, height * 0.45, 0]));
  group.add(box('chair-back', width, height * 0.55, 0.07, materials.wood, [0, height * 0.72, depth * 0.45]));
  const legW = 0.05;
  for (const x of [-width / 2 + legW, width / 2 - legW]) {
    for (const z of [-depth / 2 + legW, depth / 2 - legW]) {
      group.add(box('chair-leg', legW, height * 0.45, legW, materials.darkWood, [x, height * 0.225, z]));
    }
  }
  return tagObject3D(group, data);
}

export function createSimpleCabinet(data, materials = DEFAULT_MATERIALS) {
  const { width = 1.2, depth = 0.45, height = 1.8 } = data;
  const group = new THREE.Group();
  group.add(box('cabinet-body', width, height, depth, materials.wood, [0, height / 2, 0]));
  group.add(box('cabinet-door-left', width * 0.48, height * 0.92, 0.03, materials.darkWood, [-width * 0.24, height * 0.5, -depth * 0.52]));
  group.add(box('cabinet-door-right', width * 0.48, height * 0.92, 0.03, materials.darkWood, [width * 0.24, height * 0.5, -depth * 0.52]));
  return tagObject3D(group, data);
}

/**
 * @param {Record<string, unknown>} opening
 * @param {'door' | 'window'} type
 * @param {Record<string, unknown>} wall
 * @param {number} metersPerPixel
 * @param {Record<string, unknown> | null | undefined} [objectOverride]
 * @returns {Record<string, unknown> | null}
 */
export function openingRecordToSceneObjectData(opening, type, wall, metersPerPixel, objectOverride) {
  const wallId = String(opening.wallId ?? objectOverride?.wallId ?? '');
  if (!wallId) return null;

  const start = Array.isArray(wall.start) ? wall.start : [0, 0];
  const end = Array.isArray(wall.end) ? wall.end : [0, 0];
  const wallLengthPx = Math.hypot(end[0] - start[0], end[1] - start[1]);
  const wallLengthM = wallLengthPx * metersPerPixel;
  const t = resolveOpeningT(opening, wallLengthM > 0 ? wallLengthM : 1);
  const centerRatio = t ?? Number(opening.centerRatio ?? opening.t ?? objectOverride?.positionOnWall);
  if (!Number.isFinite(centerRatio)) return null;

  const wallThickness = positiveNumber(
    wall.thicknessMeters ?? wall.thickness ?? wall.wallThickness,
    DEFAULT_WALL_THICKNESS_M,
  );

  const id = String(opening.id ?? objectOverride?.id ?? `${type}_${wallId}_${centerRatio}`);
  const widthDefault = type === 'door' ? DEFAULT_DOOR_WIDTH_METERS : DEFAULT_WINDOW_WIDTH_METERS;
  const heightDefault = type === 'door' ? DEFAULT_DOOR_HEIGHT_METERS : DEFAULT_WINDOW_HEIGHT_METERS;

  const width = positiveNumber(
    opening.widthMeters ?? opening.width ?? objectOverride?.width,
    widthDefault,
  );
  const height = positiveNumber(
    opening.heightMeters ?? opening.height ?? objectOverride?.height,
    heightDefault,
  );

  /** @type {Record<string, unknown>} */
  const data = {
    id,
    type,
    wallId,
    centerRatio,
    positionOnWall: centerRatio,
    t: centerRatio,
    width,
    height,
    depth: wallThickness,
    frameDepth: wallThickness,
    wallThickness,
  };

  if (type === 'door') {
    const doorState =
      opening.doorState === 'open' ||
      opening.doorState === 'closed' ||
      opening.doorState === 'frame-only'
        ? opening.doorState
        : objectOverride?.doorState === 'open' ||
            objectOverride?.doorState === 'closed' ||
            objectOverride?.doorState === 'frame-only'
          ? objectOverride.doorState
          : 'closed';
    data.doorState = doorState;
    data.showPanel = doorState !== 'frame-only';
    data.openAngle = doorState === 'open' ? 90 : 0;
    const hingeSide =
      opening.hingeSide ??
      objectOverride?.hingeSide ??
      opening.swingDirection ??
      objectOverride?.swingDirection ??
      'left';
    data.hingeSide = hingeSide === 'right' ? 'right' : 'left';
    data.swingSide =
      opening.swingSide ?? objectOverride?.swingSide ?? 'inside';
    data.swingDirection = data.hingeSide;
  } else {
    data.sillHeight = nonNegativeNumber(
      opening.sillHeightMeters ?? opening.sillHeight ?? objectOverride?.sillHeight,
      DEFAULT_WINDOW_SILL_HEIGHT_METERS,
    );
    data.glassDepth = positiveNumber(
      objectOverride?.glassDepth ?? opening.glassDepth,
      Math.max(0.02, wallThickness * 0.35),
    );
  }

  return data;
}

/**
 * @param {{
 *   doors?: Array<Record<string, unknown>>;
 *   windows?: Array<Record<string, unknown>>;
 *   walls?: Array<Record<string, unknown>>;
 *   objects?: Array<Record<string, unknown>>;
 *   center?: { x: number; y: number };
 *   metersPerPixel?: number;
 * }} params
 * @returns {THREE.Object3D[]}
 */
export function buildOpeningMeshesFromRecords({
  doors = [],
  windows = [],
  walls = [],
  objects = [],
  center = { x: 0, y: 0 },
  metersPerPixel = 1,
}) {
  const wallById = new Map();
  walls.forEach((wall, index) => {
    const id = typeof wall.id === 'string' ? wall.id : `wall_${index + 1}`;
    const thicknessMeters = Number(wall.thicknessMeters);
    wallById.set(id, {
      ...wall,
      thicknessMeters:
        Number.isFinite(thicknessMeters) && thicknessMeters > 0
          ? thicknessMeters
          : DEFAULT_WALL_THICKNESS_M,
    });
  });

  const objectById = new Map();
  objects
    .filter((object) => object.type === 'door' || object.type === 'window')
    .forEach((object) => {
      if (object.id) objectById.set(String(object.id), object);
    });

  const seenIds = new Set();
  /** @type {THREE.Object3D[]} */
  const meshes = [];

  const addOpeningMesh = (opening, type) => {
    const openingId = String(opening.id ?? '');
    if (openingId && seenIds.has(openingId)) return;
    const wall = wallById.get(String(opening.wallId));
    if (!wall) return;
    const objectOverride = openingId ? objectById.get(openingId) : null;
    const sceneData = openingRecordToSceneObjectData(opening, type, wall, metersPerPixel, objectOverride);
    if (!sceneData) return;
    const mesh =
      type === 'door'
        ? createDoor3D(sceneData)
        : createWindow3D(sceneData);
    placeObjectOnWall(mesh, sceneData, wall, center, metersPerPixel);
    meshes.push(mesh);
    if (openingId) seenIds.add(openingId);
  };

  doors.forEach((door) => addOpeningMesh(door, 'door'));
  windows.forEach((windowItem) => addOpeningMesh(windowItem, 'window'));

  return meshes;
}

export function placeObjectOnWall(object3D, objectData, wall, center, metersPerPixel) {
  const t = Number(objectData.positionOnWall ?? objectData.centerRatio ?? objectData.t);
  const start = Array.isArray(wall.start) ? wall.start : [0, 0];
  const end = Array.isArray(wall.end) ? wall.end : [0, 0];
  const clampedT = Number.isFinite(t) ? Math.max(0, Math.min(1, t)) : 0.5;
  const xPx = start[0] + (end[0] - start[0]) * clampedT;
  const yPx = start[1] + (end[1] - start[1]) * clampedT;
  const x = (xPx - center.x) * metersPerPixel;
  const z = (yPx - center.y) * metersPerPixel;
  const dx = (end[0] - start[0]) * metersPerPixel;
  const dz = (end[1] - start[1]) * metersPerPixel;
  object3D.position.set(x, 0, z);
  object3D.rotation.y = -Math.atan2(dz, dx);
  return object3D;
}

export function createSceneObjectFromData(objectData, context = {}) {
  const { wallById = new Map(), center = { x: 0, y: 0 }, metersPerPixel = 1 } = context;
  let object3D = null;
  if (objectData.type === 'door' || objectData.type === 'window') {
    const wall = wallById.get(String(objectData.wallId));
    if (!wall) return null;
    const wallThickness = positiveNumber(
      wall.thicknessMeters ?? wall.thickness ?? wall.wallThickness,
      DEFAULT_WALL_THICKNESS_M,
    );
    const wallObjectData = {
      ...objectData,
      depth: wallThickness,
      frameDepth: wallThickness,
      wallThickness,
      ...(objectData.type === 'window'
        ? { glassDepth: positiveNumber(objectData.glassDepth, Math.max(0.02, wallThickness * 0.35)) }
        : {}),
    };
    object3D =
      objectData.type === 'door'
        ? createDoor3D(wallObjectData)
        : createWindow3D(wallObjectData);
    return placeObjectOnWall(object3D, wallObjectData, wall, center, metersPerPixel);
  }
  else if (objectData.type === 'furniture') {
    if (objectData.category === 'sofa') object3D = createSimpleSofa(objectData);
    else if (objectData.category === 'bed') object3D = createSimpleBed(objectData);
    else if (objectData.category === 'chair') object3D = createSimpleChair(objectData);
    else if (objectData.category === 'cabinet') object3D = createSimpleCabinet(objectData);
    else object3D = createSimpleTable(objectData);
  }
  if (!object3D) return null;

  object3D.position.set(
    (Number(objectData.x) || 0) - center.x * metersPerPixel,
    0,
    (Number(objectData.z) || 0) - center.y * metersPerPixel,
  );
  object3D.rotation.y = Number(objectData.rotationY) || 0;
  return object3D;
}
